import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import DashboardTemplate from '../templates/DashboardTemplate/DashboardTemplate';
import Toast, { showToast } from '../components/atoms/Toast/Toast';
import OnlineNotification from '../components/atoms/OnlineNotification/OnlineNotification';
import BulkEmailModal from '../components/molecules/BulkEmailModal/BulkEmailModal';
import NotificationCenter from '../components/molecules/NotificationCenter/NotificationCenter';
import authService from '../services/authService';
import useAppWebSocket from '../hooks/useWebSocket';

import { useOnlinePresence } from '../hooks/useOnlinePresence';
import { useToastNotifications } from '../hooks/useToastNotifications';
import { useNotificationCenter } from '../hooks/useNotificationCenter';
import { useLiveLogs } from '../hooks/useLiveLogs';
import { useUsers } from '../hooks/useUsers';
import { useAuthLogs } from '../hooks/useAuthLogs';
import { useSecufireLogs } from '../hooks/useSecufireLogs';

const SECUFIRE_MICROSERVICES = [
  'historic-fire_service',
  'physical_model',
  'dfci_service',
  'vegetation_service',
  'weather_service',
];
const MAX_LIVE_LOGS = 100;

const DashboardScreen = () => {
  const { isAuthenticated, accessToken, user: currentUser } = useSelector(
    (s) => s.auth
  );

  const [activeSection, setActiveSection] = useState('users');
  const activeSectionRef = useRef(activeSection);
  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  // ── Hooks personnalisés ────────────────────────────────────────────
  const presence = useOnlinePresence({ currentUser });
  const { notifications, clear } = useToastNotifications(accessToken);
  const notifCenter = useNotificationCenter();
  const liveLogs = useLiveLogs();

  const users = useUsers({
    isAuthenticated,
    accessToken,
    currentUser,
    onlineStatusRef: presence.onlineStatusRef,
  });

  const authLogs = useAuthLogs({
    isAuthenticated,
    accessToken,
    activeSection,
    fetchLiveLogs: liveLogs.fetchLiveLogs,
  });

  const secufireLogs = useSecufireLogs({
    isAuthenticated,
    accessToken,
    activeSection,
    fetchLiveLogs: liveLogs.fetchLiveLogs,
  });

  // ── Home admin select ──────────────────────────────────────────────
  const [homeSelectedAdminId, setHomeSelectedAdminId] = useState(
    currentUser?.id || null
  );
  const handleHomeAdminSelect = (admin) => {
    setHomeSelectedAdminId(admin?.id ?? null);
  };
  useEffect(() => {
    if (currentUser?.role === 'admin' && currentUser?.id && !homeSelectedAdminId) {
      setHomeSelectedAdminId(currentUser.id);
    }
  }, [currentUser, homeSelectedAdminId]);

  // ── Bulk email ──────────────────────────────────────────────────────
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);

  const handleSendBulkEmail = async (formData) => {
    setSendingBulkEmail(true);
    try {
      await authService.bulkEmail(formData);
      showToast('Email envoyé', 'success');
    } catch (e) {
      showToast(e.message || 'Erreur envoi', 'error');
      throw e;
    } finally {
      setSendingBulkEmail(false);
    }
  };

  const fetchUsersRef = useRef(null);
  useEffect(() => {
    fetchUsersRef.current = users.fetchUsers;
  }, [users.fetchUsers]);

  // ════════════════════════════════════════════════════════════════════
  //  SECTION CHANGE HANDLER  (défini AVANT notifSlot)
  // ════════════════════════════════════════════════════════════════════
  const handleSectionChange = useCallback(
    (newSection) => {
      if (newSection === activeSection) return;

      if (activeSection === 'userLogs') authLogs.reset();
      if (activeSection === 'secufireActionLogs') secufireLogs.reset();
      setActiveSection(newSection);
      if (newSection === 'users') users.fetchUsers();
    },
    /*
     * ── Fix : rafale de re-renders à l'ouverture ──
     * Avant : deps = [activeSection, authLogs, secufireLogs, users].
     * authLogs/secufireLogs/users sont les OBJETS ENTIERS renvoyés par
     * leurs hooks respectifs — si ces hooks ne mémoïsent pas leur
     * `return {...}` en interne (très courant), un nouvel objet est
     * renvoyé à CHAQUE render de DashboardScreen, même sans changement
     * réel. handleSectionChange changeait donc d'identité à chaque
     * render, ce qui invalidait tout ce qui en dépend en cascade
     * (notifSlot plus bas, donc NotificationCenter, donc un nouveau
     * render déclenché par cette invalidation, etc.). En ne dépendant
     * que des fonctions précises réellement utilisées ici — qui, elles,
     * sont généralement stabilisées via useCallback à l'intérieur de
     * ces hooks même si l'objet englobant ne l'est pas — on retrouve
     * une identité stable pour handleSectionChange tant que rien de
     * pertinent ne change vraiment.
     */
    [activeSection, authLogs.reset, secufireLogs.reset, users.fetchUsers]
  );

  // ════════════════════════════════════════════════════════════════════
  //  NOTIFICATION SLOT — monté une seule fois
  // ════════════════════════════════════════════════════════════════════
  const notifSlot = useMemo(
    () => (
      <NotificationCenter
        registerPush={notifCenter.registerNotifPush}
        onNavigate={handleSectionChange}
      />
    ),
    [notifCenter.registerNotifPush, handleSectionChange]
  );

  // ════════════════════════════════════════════════════════════════════
  //  WEBSOCKET EVENTS
  // ════════════════════════════════════════════════════════════════════
  /*
   * ── Fix : rafale de re-renders à l'ouverture ──
   * Avant, cet objet de handlers était un LITTÉRAL INLINE passé
   * directement à useAppWebSocket({...}) : une nouvelle référence
   * d'objet (et de chaque fonction à l'intérieur) était donc créée à
   * CHAQUE render de DashboardScreen. Si useAppWebSocket utilise cet
   * objet dans un useEffect pour s'abonner/désabonner au websocket
   * (pattern standard), une nouvelle référence à chaque render force
   * cet effet à se redéclencher — désabonnement + réabonnement au
   * websocket à chaque render. À l'ouverture, plusieurs fetches
   * (users, admins, hiérarchie…) se terminent en cascade en quelques
   * centaines de ms ; chacun déclenche un render de DashboardScreen,
   * chacun recréait l'objet handlers, chacun re-déclenchait l'effet
   * de useAppWebSocket → c'est la rafale de re-renders observée à
   * l'ouverture. En mémoïsant l'objet avec des dépendances précises
   * (les fonctions réellement utilisées à l'intérieur), il garde une
   * identité stable tant qu'aucune d'elles ne change vraiment.
   */
  const wsHandlers = useMemo(
    () => ({
      'activity.log': (data) => {
        const section = activeSectionRef.current;
        if (section !== 'userLogs' && section !== 'secufireActionLogs') return;
        if (section === 'secufireActionLogs') {
          const ms = data.microservice || data.service_name?.split('.')?.[0];
          if (ms && !SECUFIRE_MICROSERVICES.includes(ms)) return;
        }
        liveLogs.setLiveLogs((prev) => [data, ...prev].slice(0, MAX_LIVE_LOGS));
      },
      'activity.new': (data) => {
        authLogs.patchActivityCount(data);
        secufireLogs.patchActivityCount(data);
        liveLogs.patchActivityCount(data);
      },
      'stats.updated': (data) => {
        liveLogs.patchStatsCount(data);
        authLogs.patchUserDetail(data);
        secufireLogs.patchUserDetail(data);
      },
      'user.online': (data) => presence.handleUserOnline(data, users.setUsers),
      'user.created': () => {
        fetchUsersRef.current?.();
      },
      'user.updated': (data) => {
        fetchUsersRef.current?.();
        authLogs.invalidateDetail(data.user_id);
      },
      'user.deleted': (data) => {
        users.setUsers((prev) =>
          prev.filter((u) => String(u.id) !== String(data.user_id))
        );
      },
      'quota.updated': (data) => {
        users.setUsers((prev) =>
          prev.map((u) =>
            String(u.id) === String(data.admin_id)
              ? { ...u, nbr_max: data.nbr_max }
              : u
          )
        );
      },
      'model.used': presence.handleModelUsed,
      'notification.new': notifCenter.handleNotificationNew,
    }),
    [
      liveLogs.setLiveLogs,
      liveLogs.patchActivityCount,
      liveLogs.patchStatsCount,
      authLogs.patchActivityCount,
      authLogs.patchUserDetail,
      authLogs.invalidateDetail,
      secufireLogs.patchActivityCount,
      secufireLogs.patchUserDetail,
      presence.handleUserOnline,
      presence.handleModelUsed,
      users.setUsers,
      notifCenter.handleNotificationNew,
    ]
  );

  const { isConnected } = useAppWebSocket(wsHandlers);

  // ── Rendu ──────────────────────────────────────────────────────────
  return (
    <>
      <Toast />
      <OnlineNotification
        events={presence.onlineEvents}
        onNavigate={handleSectionChange}
      />
      <BulkEmailModal
        isOpen={bulkEmailOpen}
        onClose={() => setBulkEmailOpen(false)}
        usersList={users.users}
        currentUser={currentUser}
        onSendBulkEmail={handleSendBulkEmail}
        isSending={sendingBulkEmail}
      />
      <DashboardTemplate
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        isWsConnected={isConnected}
        homeSelectedAdminId={homeSelectedAdminId}
        onHomeAdminSelect={handleHomeAdminSelect}
        liveLogs={liveLogs.liveLogs}
        loadingLiveLogs={liveLogs.loadingLiveLogs}
        userActivityCounts={liveLogs.userActivityCounts}
        onlineStatus={presence.onlineStatus}
        users={users.users}
        loadingUsers={users.loadingUsers}
        adminsList={users.adminsList}
        hierarchyTree={users.hierarchyTree}
        loadingHierarchy={users.loadingHierarchy}
        currentUser={currentUser}
        onOpenBulkEmail={() => setBulkEmailOpen(true)}
        isSendingBulkEmail={sendingBulkEmail}
        onCreateUser={users.handleCreateUser}
        onUpdateUser={users.handleUpdateUser}
        onDeleteUser={users.handleDeleteUser}
        onSupervisionAdd={users.handleSupervisionAdd}
        onSupervisionRemove={users.handleSupervisionRemove}
        onSupervisionReplace={users.handleSupervisionReplace}
        onSupervisionReplaceAll={users.handleSupervisionReplaceAll}
        onShareHierarchy={users.handleShareHierarchy}
        onSaveUserRights={() => showToast('Droits mis à jour', 'success')}
        notifSlot={notifSlot}
        onNotifNavigate={handleSectionChange}
        {...authLogs.props}
        authAdmins={users.adminsList}
        {...secufireLogs.props}
        admins={users.adminsList}
        onToggleStatus={users.handleToggleStatus}
      />
    </>
  );
};

export default DashboardScreen;